import { isTableMetadataVariableType } from "@/types/metadataVariableType";

export const DIRECTIVE_VARIABLE_TYPES = [
  "number",
  "integer",
  "list",
  "table",
  "text",
  "boolean",
] as const;

export type DirectiveVariableType = (typeof DIRECTIVE_VARIABLE_TYPES)[number];

export interface DirectiveChoiceOption {
  value: string;
  label: string;
}

export interface DirectiveChoiceSlot {
  label: string;
  placeholder?: string;
  /**
   * Strict allowed values only: UI shows a dropdown; user must pick one.
   * Option with value "" is the default (no segment in raw). At most one of options or suggestedOptions per slot.
   */
  options?: readonly DirectiveChoiceOption[];
  /**
   * Suggested values + type your own: UI shows an input with suggestions; user may pick one or type any value.
   * At most one of options or suggestedOptions per slot.
   */
  suggestedOptions?: readonly DirectiveChoiceOption[];
}

export interface DirectiveConfigItem {
  key: string;
  label: string;
  variableTypes: DirectiveVariableType[];
  choiceSlots: DirectiveChoiceSlot[];
  exampleNotation: string;
  exampleSparx: string;
  exampleRendered: string;
}

/** Placeholders for if-singular choice slots (number/integer). Exported for tests/stories. */
export const IF_SINGULAR_PLACEHOLDER_SINGULAR = "e.g. year";
export const IF_SINGULAR_PLACEHOLDER_PLURAL = "e.g. years";

export const DIRECTIVE_CONFIG: DirectiveConfigItem[] = [
  {
    key: "if-singular",
    label: "if-singular",
    variableTypes: ["number", "integer"],
    choiceSlots: [
      { label: "Singular", placeholder: IF_SINGULAR_PLACEHOLDER_SINGULAR },
      { label: "Plural", placeholder: IF_SINGULAR_PLACEHOLDER_PLURAL },
    ],
    exampleNotation: "{{@if-singular/variable:singularVersion:pluralVersion}}",
    exampleSparx:
      "It's been 5 {{@if-singular/LoanAge:year:years}} since you took out your {{LoanName}}.",
    exampleRendered:
      "It's been 5 years since you took out your Batmobile Loan.",
  },
  {
    key: "if-singular(list)",
    label: "if-singular(list)",
    variableTypes: ["list"],
    choiceSlots: [
      { label: "Singular", placeholder: "e.g. offers" },
      { label: "Plural", placeholder: "e.g. offer" },
    ],
    exampleNotation:
      "{{@if-singular(list)/variable:singularVersion:pluralVersion}}",
    exampleSparx:
      "Costco and Sam's Club {{@if-singular(list)/WholesaleClubs:offers:offer}} cheaper gas prices.",
    exampleRendered: "Costco and Sam's Club offer cheaper gas prices.",
  },
  {
    key: "if-singular(boolean)",
    label: "if-singular(boolean)",
    variableTypes: ["boolean"],
    choiceSlots: [
      { label: "Singular", placeholder: "e.g. was approved" },
      { label: "Plural", placeholder: "e.g. was not approved" },
    ],
    exampleNotation:
      "{{@if-singular(boolean)/variable:singularVersion:pluralVersion}}",
    exampleSparx:
      "Your application {{@if-singular(boolean)/IsApproved:was approved:was not approved}}.",
    exampleRendered: "Your application was approved.",
  },
  {
    key: "conditional-text(greater)",
    label: "conditional-text(greater)",
    variableTypes: ["number", "integer"],
    choiceSlots: [
      { label: "Threshold", placeholder: "e.g. 2" },
      {
        label: "Value if greater",
        placeholder: "e.g. Multiple items will ship together.",
      },
    ],
    exampleNotation:
      "{{@conditional-text(greater)/variable:threshold:valueIfGreater}}",
    exampleSparx:
      "Your order is being prepared. {{@conditional-text(greater)/ItemCount:2:Multiple items will ship together. }}",
    exampleRendered:
      "Your order is being prepared. Multiple items will ship together.",
  },
  {
    key: "conditional-text(equal)",
    label: "conditional-text(equal)",
    variableTypes: ["number", "integer"],
    choiceSlots: [
      { label: "Required count", placeholder: "e.g. 2" },
      {
        label: "Value if equal",
        placeholder: "e.g. You have submitted all the information required.",
      },
    ],
    exampleNotation: "{{@conditional-text(equal)/variable:value:valueIfEqual}}",
    exampleSparx:
      "Your application status: {{@conditional-text(equal)/DocumentsSubmitted:2:You have submitted all the information required. }}",
    exampleRendered:
      "Your application status: You have submitted all the information required.",
  },
  {
    key: "non-breaking-space",
    label: "non-breaking-space",
    variableTypes: ["text"],
    choiceSlots: [],
    exampleNotation: "{{@non-breaking-space/variable}}",
    exampleSparx:
      "Use the {{@non-breaking-space/ProgramName}} program to get started.",
    exampleRendered: "Use the Spend\u00A0'n'\u00A0Save program to get started.",
  },
  {
    key: "print(list)",
    label: "print(list)",
    variableTypes: ["list"],
    choiceSlots: [
      {
        label: "Conjunction",
        placeholder: "e.g. and or or",
        suggestedOptions: [
          { value: "and", label: "and" },
          { value: "or", label: "or" },
        ],
      },
    ],
    exampleNotation:
      "{{@print(list)/variable:or}} or {{@print(list)/variable:and}}",
    exampleSparx:
      "If you think {{@print(list)/AlternativeCashBackCategories:or}} would suit you better, we can switch.",
    exampleRendered:
      "If you think groceries, pharmacy, or gas would suit you better, we can switch.",
  },
  {
    key: "print(table)",
    label: "print(table)",
    variableTypes: ["table"],
    // Choice slots are derived at runtime from the selected table variable's columns (see getTableVariableColumns / getPrintTableChoiceSlotForIndex).
    choiceSlots: [],
    exampleNotation: "{{@print(table)/variable:columnName1:columnName2:...}}",
    exampleSparx:
      "Summary: {{@print(table)/ExternalMortgageLenderDetails:Lender:Years Open:Last Monthly Payment}}.",
    exampleRendered:
      "Summary: | Lender | Years Open | Last Monthly Payment |\n| --- | --- | --- |\n| Acme Lending | 3 | $1,200.00 |",
  },
  {
    key: "number-to-text",
    label: "number-to-text",
    variableTypes: ["number", "integer"],
    choiceSlots: [],
    exampleNotation: "{{@number-to-text/variable}}",
    exampleSparx:
      "You've had {{@number-to-text/PeriodATMFees}} months of no ATM fees.",
    exampleRendered: "You've had five months of no ATM fees.",
  },
  {
    key: "lower-case",
    label: "lower-case",
    variableTypes: ["text"],
    choiceSlots: [],
    exampleNotation: "{{@lower-case/variable}}",
    exampleSparx: "Use {{@lower-case/ProgramName}} to complete your goals.",
    exampleRendered: "Use spend 'n' save to complete your goals.",
  },
  {
    key: "capitalize",
    label: "capitalize",
    variableTypes: ["text"],
    choiceSlots: [
      {
        label: "Per-word mode (optional)",
        options: [
          { value: "", label: "Sentence style (default)" },
          { value: "word", label: "Per word" },
        ],
      },
    ],
    exampleNotation:
      "{{@capitalize/variable}} or {{@capitalize/variable:word}}",
    exampleSparx: "{{@capitalize/ProgramName}} is a great fit for your goals.",
    exampleRendered: "Spend 'n' Save is a great fit for your goals.",
  },
];

const DIRECTIVE_BY_KEY = new Map(
  DIRECTIVE_CONFIG.map((d) => [d.key, d] as const),
);

export const getDirectiveConfig = (
  key: string,
): DirectiveConfigItem | undefined => DIRECTIVE_BY_KEY.get(key);

/** Set of variable types present in the given variables array. */
const getVariableTypesPresent = (
  variables: { type?: string }[],
): Set<string> => {
  const set = new Set<string>();
  for (const v of variables) {
    if (typeof v.type === "string" && v.type.length > 0) {
      set.add(v.type);
    }
  }
  return set;
};

/**
 * Directives available given the current sparx variables (by type).
 * Used by the @ suggestion to show only directives that have at least one matching variable type.
 */
export const getAvailableDirectives = (
  variables: { type?: string }[],
): DirectiveConfigItem[] => {
  const typesPresent = getVariableTypesPresent(variables);
  if (typesPresent.size === 0) {
    return [];
  }
  return DIRECTIVE_CONFIG.filter((config) =>
    config.variableTypes.some((t) => typesPresent.has(t)),
  );
};

/** Column shape used by table variables in metadata (name required). */
export interface TableVariableColumn {
  name: string;
  type?: string;
}

/**
 * Resolve a table variable by name and return its columns when type is "table" and columns exist.
 * Used by print(table) to derive allowed column options.
 */
export const getTableVariableColumns = (
  variables: {
    name?: string;
    type?: string;
    columns?: TableVariableColumn[];
  }[],
  variableName: string,
): TableVariableColumn[] | undefined => {
  const v = variables.find(
    (x) => typeof x.name === "string" && x.name === variableName,
  );
  if (v == null || !isTableMetadataVariableType(v.type)) return undefined;
  const cols = v.columns;
  if (!Array.isArray(cols) || cols.length === 0) return undefined;
  return cols;
};

/**
 * For print(table), choice slots are derived at runtime from the selected table variable's columns.
 * Returns the single choice slot for the given index: label and options (column names not yet chosen in other slots).
 */
export const getPrintTableChoiceSlotForIndex = (
  columns: TableVariableColumn[],
  existingChoices: string[],
  choiceIndex: number,
): DirectiveChoiceSlot => {
  const columnNames = columns.map((c) => c.name);
  const chosenSet = new Set(
    existingChoices.filter((_, i) => i !== choiceIndex),
  );
  const remaining = columnNames.filter((name) => !chosenSet.has(name));
  const options: DirectiveChoiceOption[] = remaining.map((name) => ({
    value: name,
    label: name,
  }));
  return {
    label: `Column ${choiceIndex + 1}`,
    options,
    ...(choiceIndex === 0 ? { placeholder: "Select a column…" } : {}),
  };
};
